import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const TRUCK_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';

const GIFT_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v14"/><path d="M20 11v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8"/><path d="M7.5 7a1 1 0 0 1 0-5A4.8 8 0 0 1 12 7a4.8 8 0 0 1 4.5-5 1 1 0 0 1 0 5"/><rect x="3" y="7" width="18" height="4" rx="1"/></svg>';

/** @typedef {{ productHandle: string, variantIds: number[] }} GiftOption */
/**
 * A tier's `slots` array holds one entry per *simultaneous, mandatory* gift.
 * Each slot is itself a list of alternative GiftOptions the customer can pick
 * between for that slot (usually just one). A tier with two slots (e.g. an
 * adult shirt slot + a youth shirt slot) grants both together, as a set.
 * @typedef {{ thresholdCents: number, label: string, variantIds: number[], slots: GiftOption[][] }} Tier
 */
/** @typedef {{ title: string, image: string | null, variants: { id: number, title: string, price: number, available: boolean }[] }} GiftProduct */

/** @returns {Tier[]} sorted ascending by thresholdCents */
function parseTiersConfig() {
  const el = document.getElementById('cart-rewards-data');
  if (!el?.textContent) return [];
  try {
    const raw = JSON.parse(el.textContent);
    if (!raw || !Array.isArray(raw.tiers)) return [];
    return raw.tiers
      .map((t) => {
        if (typeof t.thresholdCents !== 'number') return null;

        /** @param {any} opts @returns {GiftOption[]} */
        const parseOptions = (opts) =>
          Array.isArray(opts)
            ? opts.filter((o) => o && typeof o.productHandle === 'string' && Array.isArray(o.variantIds))
            : [];

        /** @type {GiftOption[][]} */
        let slots = [];
        if (Array.isArray(t.slots)) {
          // Multiple simultaneous mandatory gifts, e.g. a matching adult + youth set.
          slots = t.slots.map(parseOptions).filter((slotOptions) => slotOptions.length > 0);
        } else if (Array.isArray(t.giftOptions)) {
          // Single gift slot with alternative product choices.
          const giftOptions = parseOptions(t.giftOptions);
          if (giftOptions.length) slots = [giftOptions];
        } else if (typeof t.giftProductHandle === 'string' && Array.isArray(t.variantIds)) {
          // Legacy single-product shape.
          slots = [[{ productHandle: t.giftProductHandle, variantIds: t.variantIds }]];
        }

        return {
          thresholdCents: t.thresholdCents,
          label: typeof t.label === 'string' ? t.label : 'Free gift',
          slots,
          variantIds: slots.flatMap((slotOptions) => slotOptions.flatMap((o) => o.variantIds)),
        };
      })
      .filter((t) => t !== null)
      .sort((a, b) => a.thresholdCents - b.thresholdCents);
  } catch {
    return [];
  }
}

/** Module-level cache shared by both components. @type {Map<string, GiftProduct>} */
const productCache = new Map();

/**
 * @param {string} productHandle
 * @param {number[]} eligibleVariantIds
 * @returns {Promise<GiftProduct | null>}
 */
async function fetchProductByHandle(productHandle, eligibleVariantIds) {
  let product = productCache.get(productHandle);
  if (!product) {
    const res = await fetch(`/products/${productHandle}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to fetch product ${productHandle}: ${res.status}`);
    const raw = await res.json();
    const eligible = new Set(eligibleVariantIds);
    product = {
      title: raw.title,
      url: raw.url,
      image: raw.featured_image ?? raw.images?.[0] ?? null,
      variants: raw.variants
        .filter((v) => eligible.has(v.id))
        .map((v) => ({ id: v.id, title: v.title, price: v.price, available: v.available })),
    };
    productCache.set(productHandle, product);
  }
  return product;
}

const FADE_MS = 180;

/**
 * Cross-fades a host element's content: fades out, swaps children, fades
 * back in. Both `cart-rewards-bar` and `cart-rewards-section` carry the
 * `.cart-rewards-fade-out` opacity transition in the stylesheet.
 * @param {HTMLElement} host
 * @param {Node | null} content pass null to clear (fades out and stays empty)
 */
async function fadeReplace(host, content) {
  host.classList.add('cart-rewards-fade-out');
  await new Promise((resolve) => setTimeout(resolve, FADE_MS));
  if (content == null) host.replaceChildren();
  else host.replaceChildren(content);
  host.classList.remove('cart-rewards-fade-out');
}

/** @returns {string[]} sectionIds for every cart-items-component on the page */
function cartItemsSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
    .filter((id) => Boolean(id));
}

/**
 * @param {number} variantId
 * @param {string[]} sectionIds
 * @param {boolean} [confirmed] set _gift_confirmed too — used when the customer explicitly confirms their size/color, as opposed to the initial auto-claimed default
 */
async function addGiftLine(variantId, sectionIds, confirmed = false) {
  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', '1');
  body.append('properties[_free_gift]', 'true');
  if (confirmed) body.append('properties[_gift_confirmed]', 'true');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body,
  }).then((r) => r.json());
  return response;
}

/** @returns {Promise<any>} the current cart, freshly fetched */
async function fetchCart() {
  return fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((r) => r.json());
}

const DECLINED_SLOTS_ATTRIBUTE = '_declined_gift_slots';

/** @param {number} thresholdCents @param {number} slotIndex @returns {string} */
function declinedSlotKey(thresholdCents, slotIndex) {
  return `${thresholdCents}:${slotIndex}`;
}

/**
 * Declined slots are tracked as a cart attribute (not a line property) so
 * that declining a gift can genuinely remove its line — no $0 line item
 * ships for a gift the customer said no to — while still telling the sticky
 * bar's auto-claim not to immediately re-add it. Declining is per-slot: in a
 * multi-slot tier (e.g. an adult + youth matching set), each item can be
 * declined independently.
 * @param {any} cart
 * @returns {string[]} `${thresholdCents}:${slotIndex}` keys of declined slots
 */
function parseDeclinedSlots(cart) {
  const raw = cart?.attributes?.[DECLINED_SLOTS_ATTRIBUTE];
  if (typeof raw !== 'string' || !raw) return [];
  return raw.split(',').filter(Boolean);
}

/**
 * @param {Set<string>} declinedKeys `${thresholdCents}:${slotIndex}` keys
 * @param {string[]} sectionIds
 */
async function setDeclinedSlots(declinedKeys, sectionIds) {
  return fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', {
      body: JSON.stringify({
        attributes: { [DECLINED_SLOTS_ATTRIBUTE]: Array.from(declinedKeys).join(',') },
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    })
  ).then((r) => r.json());
}

/**
 * @param {{ key: string }} line
 * @param {string[]} sectionIds
 */
async function removeGiftLine(line, sectionIds) {
  return fetch(
    Theme.routes.cart_change_url,
    fetchConfig('json', {
      body: JSON.stringify({
        id: line.key,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    })
  ).then((r) => r.json());
}

/**
 * @param {'add' | 'remove'} action
 * @param {EventTarget} target
 * @param {{ merchandiseId?: string, id?: string, quantity: number }} line
 * @param {Record<string, string> | undefined} sections
 */
async function dispatchCartLinesUpdate(action, target, line, sections) {
  const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((r) => r.json());

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart-rewards',
      lines: [line],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, source: 'cart-rewards-component', didError: false, sections },
      }),
    })
  );
}

/**
 * Sticky top bar: overall progress toward the highest tier, plus one small
 * card per tier. Also owns reconciliation — auto-claims the default variant
 * for any newly-earned gift tier, and auto-removes gift lines that fall out
 * of eligibility (e.g. after removing cart items). This is the only
 * reconciling half; `<cart-rewards-section>` is a read-only display of the
 * result.
 *
 * This is client-side UX only. The authoritative gate for gift tiers is
 * server-side: the cart-transform Function prices every `_free_gift` line to
 * $0, and cart-validation blocks checkout if a `_free_gift` line is present
 * but unearned or not a configured gift variant.
 *
 * @extends {HTMLElement}
 */
class CartRewardsBarComponent extends HTMLElement {
  /** @type {Tier[]} */
  #tiers = [];
  /** @type {boolean} */
  #busy = false;
  /** @type {boolean} another refresh was requested while one was in flight */
  #refreshRequested = false;
  /** @type {string | null} */
  #lastRenderKey = null;
  /** @type {Set<string>} productHandle+threshold keys currently mid-claim, to avoid duplicate concurrent claims */
  #claiming = new Set();
  /** @type {{ wrapper: HTMLElement, statusLabel: HTMLElement, statusAmount: HTMLElement, fill: HTMLElement, markers: HTMLElement[], tierCards: HTMLElement[] } | null} cached DOM refs, built once so CSS transitions (width/color) can animate instead of restarting from scratch every update */
  #dom = null;

  connectedCallback() {
    this.#tiers = parseTiersConfig();
    if (!this.#tiers.length) return;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** @param {CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    if (event.target === this) return;
    event.promise?.then(() => this.#refresh()).catch(() => {});
  };

  /**
   * Coalescing guard: if a refresh arrives while one is already in flight,
   * remember it and run one more trailing pass once the current one settles,
   * instead of silently dropping it. Without this, rapid consecutive cart
   * updates (e.g. clicking +/- quickly) can leave the bar showing a stale
   * state that only catches up on some later, unrelated event.
   */
  async #refresh() {
    if (this.#busy) {
      this.#refreshRequested = true;
      return;
    }
    this.#busy = true;
    try {
      do {
        this.#refreshRequested = false;
        await this.#refreshOnce();
      } while (this.#refreshRequested);
    } finally {
      this.#busy = false;
    }
  }

  async #refreshOnce() {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      const giftLines = cart.items.filter((item) => item.properties && '_free_gift' in item.properties);
      const giftVariantIds = new Set(giftLines.map((item) => item.variant_id));
      const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const qualifyingCents = Math.max(0, cart.total_price - giftTotal);
      const declinedSlots = new Set(parseDeclinedSlots(cart));

      // Remove gift lines whose tier is no longer earned.
      for (const line of giftLines) {
        const tier = this.#tiers.find((t) => t.variantIds.includes(line.variant_id));
        const earned = tier != null && qualifyingCents >= tier.thresholdCents;
        if (!earned) await this.#removeLine(line);
      }

      // A tier that's fallen out of eligibility "forgets" any prior declines
      // on its slots — re-earning it later (a different cart, effectively)
      // should default back to auto-claim rather than staying declined
      // forever.
      let unearnedDeclineCleared = false;
      for (const tier of this.#tiers) {
        if (qualifyingCents >= tier.thresholdCents) continue;
        for (let slotIndex = 0; slotIndex < tier.slots.length; slotIndex++) {
          const key = declinedSlotKey(tier.thresholdCents, slotIndex);
          if (declinedSlots.has(key)) {
            declinedSlots.delete(key);
            unearnedDeclineCleared = true;
          }
        }
      }
      if (unearnedDeclineCleared) {
        await setDeclinedSlots(declinedSlots, cartItemsSectionIds());
      }

      // Auto-claim the default variant for each not-yet-fulfilled,
      // not-declined slot of any newly-earned gift tier. A multi-slot tier
      // (e.g. an adult + youth matching set) claims each slot independently.
      for (const tier of this.#tiers) {
        if (!tier.slots.length) continue;
        const earned = qualifyingCents >= tier.thresholdCents;
        if (!earned) continue;
        for (let slotIndex = 0; slotIndex < tier.slots.length; slotIndex++) {
          const slot = tier.slots[slotIndex];
          const slotVariantIds = slot.flatMap((o) => o.variantIds);
          const fulfilled = slotVariantIds.some((id) => giftVariantIds.has(id));
          const declined = declinedSlots.has(declinedSlotKey(tier.thresholdCents, slotIndex));
          const claimKey = declinedSlotKey(tier.thresholdCents, slotIndex);
          if (!fulfilled && !declined && !this.#claiming.has(claimKey)) {
            this.#claiming.add(claimKey);
            try {
              await this.#autoClaimSlot(slot);
            } finally {
              this.#claiming.delete(claimKey);
            }
          }
        }
      }

      const earnedStates = this.#tiers.map((t) => qualifyingCents >= t.thresholdCents);
      const key = `${earnedStates.join(',')}:${earnedStates.every(Boolean) ? '' : Math.round(qualifyingCents / 100)}`;
      if (key === this.#lastRenderKey) return;
      this.#lastRenderKey = key;

      this.#render(qualifyingCents);
    } catch (error) {
      console.warn('[cart-rewards-bar] refresh failed:', error);
    }
  }

  /** Build the DOM structure once; subsequent calls only patch values. */
  #buildDom() {
    const wrapper = document.createElement('div');
    wrapper.className = 'cart-rewards-bar';

    const statusRow = document.createElement('div');
    statusRow.className = 'cart-rewards-bar__status';
    const statusLabel = document.createElement('span');
    const statusAmount = document.createElement('span');
    statusRow.append(statusLabel, statusAmount);
    wrapper.appendChild(statusRow);

    const track = document.createElement('div');
    track.className = 'cart-rewards-bar__track';
    const fill = document.createElement('div');
    fill.className = 'cart-rewards-bar__fill';
    fill.setAttribute('role', 'progressbar');
    fill.setAttribute('aria-valuemin', '0');
    fill.setAttribute('aria-valuemax', '100');
    fill.setAttribute('aria-label', 'Progress toward rewards');
    track.appendChild(fill);

    const maxThreshold = this.#tiers[this.#tiers.length - 1].thresholdCents;
    const markers = this.#tiers.map((tier) => {
      const marker = document.createElement('div');
      marker.className = 'cart-rewards-bar__marker';
      marker.style.left = `${Math.min(100, (tier.thresholdCents / maxThreshold) * 100)}%`;
      track.appendChild(marker);
      return marker;
    });
    wrapper.appendChild(track);

    const tierRow = document.createElement('div');
    tierRow.className = 'cart-rewards-bar__tier-row';
    const tierCards = this.#tiers.map((tier) => {
      const tierCard = document.createElement('div');
      tierCard.className = 'cart-rewards-bar__tier-card';
      const tierIcon = document.createElement('span');
      tierIcon.className = 'cart-rewards-bar__tier-icon';
      tierIcon.innerHTML = tier.variantIds.length ? GIFT_ICON_SVG : TRUCK_ICON_SVG;
      tierCard.appendChild(tierIcon);
      const tierText = document.createElement('div');
      const tierAmount = document.createElement('div');
      tierAmount.className = 'cart-rewards-bar__tier-amount';
      tierAmount.textContent = `$${(tier.thresholdCents / 100).toFixed(0)}`;
      const tierLabel = document.createElement('div');
      tierLabel.className = 'cart-rewards-bar__tier-label';
      tierLabel.textContent = tier.label;
      tierText.append(tierAmount, tierLabel);
      tierCard.appendChild(tierText);
      tierRow.appendChild(tierCard);
      return tierCard;
    });
    wrapper.appendChild(tierRow);

    this.replaceChildren(wrapper);
    this.#dom = { wrapper, statusLabel, statusAmount, fill, markers, tierCards };
  }

  /** @param {number} qualifyingCents */
  #render(qualifyingCents) {
    if (!this.#dom) this.#buildDom();
    const dom = /** @type {NonNullable<typeof this.#dom>} */ (this.#dom);

    const maxThreshold = this.#tiers[this.#tiers.length - 1].thresholdCents;
    const allEarned = this.#tiers.every((t) => qualifyingCents >= t.thresholdCents);

    if (allEarned) {
      dom.statusLabel.textContent = 'All rewards unlocked! 🎉';
      dom.statusAmount.textContent = '';
    } else {
      dom.statusLabel.textContent = 'Unlock free rewards';
      dom.statusAmount.textContent = `$${(Math.min(qualifyingCents, maxThreshold) / 100).toFixed(0)} / $${(maxThreshold / 100).toFixed(0)}`;
    }

    const pct = allEarned ? 100 : Math.min(100, Math.round((qualifyingCents / maxThreshold) * 100));
    dom.fill.style.width = `${pct}%`;
    dom.fill.setAttribute('aria-valuenow', String(pct));

    this.#tiers.forEach((tier, i) => {
      const earned = qualifyingCents >= tier.thresholdCents;
      dom.markers[i].classList.toggle('cart-rewards-bar__marker--earned', earned);
      dom.tierCards[i].classList.toggle('cart-rewards-bar__tier-card--earned', earned);
    });
  }

  /** @param {GiftOption[]} slot */
  async #autoClaimSlot(slot) {
    const firstOption = slot[0];
    if (!firstOption) return;
    let product = null;
    try {
      product = await fetchProductByHandle(firstOption.productHandle, firstOption.variantIds);
    } catch (error) {
      console.warn('[cart-rewards-bar] could not load gift product:', error);
    }
    const defaultVariantId =
      product?.variants.find((v) => v.available)?.id ?? product?.variants[0]?.id ?? firstOption.variantIds[0];
    if (defaultVariantId == null) return;
    await this.#claimGift(defaultVariantId);
  }

  /** @param {number} variantId */
  async #claimGift(variantId) {
    try {
      const sectionIds = cartItemsSectionIds();
      const response = await addGiftLine(variantId, sectionIds);
      if (response.status) {
        console.warn('[cart-rewards-bar] could not claim gift:', response.description);
        return;
      }
      await dispatchCartLinesUpdate('add', this, { merchandiseId: String(variantId), quantity: 1 }, response.sections);
    } catch (error) {
      console.warn('[cart-rewards-bar] claim failed:', error);
    }
  }

  /** @param {{ key: string }} line */
  async #removeLine(line) {
    try {
      const sectionIds = cartItemsSectionIds();
      const response = await removeGiftLine(line, sectionIds);
      await dispatchCartLinesUpdate('remove', this, { id: line.key, quantity: 0 }, response.sections);
    } catch (error) {
      console.warn('[cart-rewards-bar] could not remove unearned gift:', error);
    }
  }
}

/**
 * Scrollable "Rewards" section: lists only tiers that are actually earned,
 * placed after the cart line items so it scrolls with them beneath the
 * sticky `<cart-rewards-bar>`. Read-only — reconciliation (add/remove gift
 * lines) is owned entirely by `<cart-rewards-bar>`.
 *
 * @extends {HTMLElement}
 */
class CartRewardsSectionComponent extends HTMLElement {
  /** @type {Tier[]} */
  #tiers = [];
  /** @type {boolean} */
  #busy = false;
  /** @type {boolean} another refresh was requested while one was in flight */
  #refreshRequested = false;
  /** @type {string | null} */
  #lastRenderKey = null;

  connectedCallback() {
    this.#tiers = parseTiersConfig();
    if (!this.#tiers.length) return;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** @param {CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    if (event.target === this) return;
    event.promise?.then(() => this.#refresh()).catch(() => {});
  };

  /** Coalescing guard — see `CartRewardsBarComponent#refresh` for why this matters. */
  async #refresh() {
    if (this.#busy) {
      this.#refreshRequested = true;
      return;
    }
    this.#busy = true;
    try {
      do {
        this.#refreshRequested = false;
        await this.#refreshOnce();
      } while (this.#refreshRequested);
    } finally {
      this.#busy = false;
    }
  }

  /**
   * User-initiated actions (confirm/swap/claim) share `#busy` with the
   * background reconciliation refresh. Rather than silently dropping a click
   * that lands while a refresh is in flight (which reads as "nothing
   * happened" and forces a second click), wait for the current operation to
   * finish before proceeding.
   */
  async #waitUntilIdle() {
    while (this.#busy) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  async #refreshOnce() {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      const giftLines = cart.items.filter((item) => item.properties && '_free_gift' in item.properties);
      const giftVariantIds = new Set(giftLines.map((item) => item.variant_id));
      const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const qualifyingCents = Math.max(0, cart.total_price - giftTotal);

      const earnedTiers = this.#tiers.filter((t) => qualifyingCents >= t.thresholdCents);
      const declinedSlots = new Set(parseDeclinedSlots(cart));
      const stateKey = giftLines
        .map((l) => `${l.variant_id}:${l.properties?._gift_confirmed != null}`)
        .sort()
        .join(',');
      const key = `${earnedTiers.map((t) => t.thresholdCents).join(',')}:${stateKey}:${Array.from(declinedSlots).sort().join(',')}`;
      if (key === this.#lastRenderKey) return;
      this.#lastRenderKey = key;

      await this.#render(earnedTiers, giftLines, declinedSlots);
    } catch (error) {
      console.warn('[cart-rewards-section] refresh failed:', error);
    }
  }

  /**
   * @param {Tier[]} earnedTiers
   * @param {any[]} giftLines raw /cart.js line items carrying `_free_gift`
   * @param {Set<string>} declinedSlots `${thresholdCents}:${slotIndex}` keys of declined slots
   */
  async #render(earnedTiers, giftLines, declinedSlots) {
    if (!earnedTiers.length) {
      await fadeReplace(this, null);
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'cart-rewards-section';

    const sectionLabel = document.createElement('p');
    sectionLabel.className = 'cart-rewards-section__label';
    sectionLabel.textContent = 'Rewards';
    wrapper.appendChild(sectionLabel);

    for (const tier of earnedTiers) {
      const row = await this.#renderRow(tier, giftLines, declinedSlots);
      wrapper.appendChild(row);
    }

    await fadeReplace(this, wrapper);
  }

  /**
   * @param {Tier} tier
   * @param {any[]} giftLines
   * @param {Set<string>} declinedSlots `${thresholdCents}:${slotIndex}` keys of declined slots
   * @returns {Promise<HTMLElement>}
   */
  async #renderRow(tier, giftLines, declinedSlots) {
    // Reward-only tier (e.g. Free Shipping): no product, no cart line.
    if (!tier.variantIds.length) {
      const row = document.createElement('div');
      row.className = 'cart-rewards-section__row';

      const icon = document.createElement('span');
      icon.className = 'cart-rewards-section__row-icon';
      icon.innerHTML = TRUCK_ICON_SVG;
      row.appendChild(icon);

      const info = document.createElement('div');
      info.className = 'cart-rewards-section__info';
      const titleRow = document.createElement('p');
      titleRow.className = 'cart-rewards-section__title';
      titleRow.textContent = tier.label;
      const badge = document.createElement('span');
      badge.className = 'cart-rewards-section__badge';
      badge.textContent = 'UNLOCKED';
      titleRow.appendChild(badge);
      info.appendChild(titleRow);
      row.appendChild(info);

      const price = document.createElement('p');
      price.className = 'cart-rewards-section__row-price';
      price.textContent = 'Free';
      row.appendChild(price);

      return row;
    }

    const card = document.createElement('div');
    card.className = 'cart-rewards-section__card';
    if (!tier.slots.length) return card;

    // Tier label doubles as the card eyebrow for a multi-slot tier (a
    // matching set), shown once above every slot's item rather than repeated
    // per item.
    const eyebrow = document.createElement('p');
    eyebrow.className = 'cart-rewards-section__eyebrow';
    eyebrow.textContent = tier.slots.length > 1 ? tier.label : 'Free Gift';
    card.appendChild(eyebrow);

    const stack = document.createElement('div');
    stack.className = 'cart-rewards-section__option-stack';

    for (let slotIndex = 0; slotIndex < tier.slots.length; slotIndex++) {
      const slot = tier.slots[slotIndex];

      if (declinedSlots.has(declinedSlotKey(tier.thresholdCents, slotIndex))) {
        const row = document.createElement('div');
        row.className = 'cart-rewards-section__card-row cart-rewards-section__card-row--declined';

        const info = document.createElement('div');
        info.className = 'cart-rewards-section__card-info';
        const msg = document.createElement('p');
        msg.className = 'cart-rewards-section__optedout-msg';
        msg.textContent = "You skipped this gift — it won't be included at checkout.";
        info.appendChild(msg);
        const undoButton = document.createElement('button');
        undoButton.type = 'button';
        undoButton.className = 'cart-rewards-section__optedout-undo button-secondary';
        undoButton.textContent = "Actually, I'll take it";
        undoButton.addEventListener('click', () => this.#undeclineSlot(tier, slotIndex));
        info.appendChild(undoButton);
        row.appendChild(info);

        stack.appendChild(row);
        continue;
      }

      /** @type {(GiftProduct & { productHandle: string })[]} */
      const options = [];
      for (const opt of slot) {
        try {
          const product = await fetchProductByHandle(opt.productHandle, opt.variantIds);
          if (product) options.push({ ...product, productHandle: opt.productHandle });
        } catch (error) {
          console.warn('[cart-rewards-section] could not load gift product:', opt.productHandle, error);
        }
      }
      if (!options.length) continue;

      const slotVariantIds = new Set(slot.flatMap((o) => o.variantIds));
      const claimedLine = giftLines.find((line) => slotVariantIds.has(line.variant_id));
      const activeOption =
        options.find((o) => o.variants.some((v) => v.id === claimedLine?.variant_id)) ?? options[0];

      for (const option of options) {
      const isActive = option === activeOption;
      const optionCard = document.createElement('div');
      optionCard.className = `cart-rewards-section__option${isActive ? ' cart-rewards-section__option--active' : ''}`;

      const optionRow = document.createElement('div');
      optionRow.className = 'cart-rewards-section__card-row';

      if (option.image) {
        const imageWrap = option.url ? document.createElement('a') : document.createElement('div');
        imageWrap.className = 'cart-rewards-section__image-wrap';
        if (option.url) {
          /** @type {HTMLAnchorElement} */ (imageWrap).href = option.url;
          /** @type {HTMLAnchorElement} */ (imageWrap).setAttribute('aria-label', `View ${option.title} details`);
        }
        const img = document.createElement('img');
        img.className = 'cart-rewards-section__image';
        img.src = option.image;
        img.alt = option.title;
        img.loading = 'lazy';
        img.width = 72;
        img.height = 72;
        imageWrap.appendChild(img);
        if (isActive) {
          const qtyBadge = document.createElement('span');
          qtyBadge.className = 'cart-rewards-section__qty-badge';
          qtyBadge.textContent = '1';
          imageWrap.appendChild(qtyBadge);
        }
        optionRow.appendChild(imageWrap);
      }

      const info = document.createElement('div');
      info.className = 'cart-rewards-section__card-info';

      const title = option.url ? document.createElement('a') : document.createElement('p');
      title.className = 'cart-rewards-section__card-title';
      title.textContent = option.title;
      if (option.url) /** @type {HTMLAnchorElement} */ (title).href = option.url;
      info.appendChild(title);

      if (isActive && claimedLine) {
        const select = document.createElement('select');
        select.className = 'cart-rewards-section__select';
        select.setAttribute('aria-label', `${option.title} size/color`);
        for (const variant of option.variants) {
          const variantOpt = document.createElement('option');
          variantOpt.value = String(variant.id);
          variantOpt.textContent = variant.available ? variant.title : `${variant.title} (sold out)`;
          variantOpt.disabled = !variant.available;
          variantOpt.selected = variant.id === claimedLine.variant_id;
          select.appendChild(variantOpt);
        }
        select.addEventListener('change', () => {
          const newVariantId = Number(select.value);
          if (newVariantId && newVariantId !== claimedLine.variant_id) {
            this.#swapVariant(claimedLine, newVariantId);
          }
        });
        info.appendChild(select);

        const confirmed = claimedLine.properties?._gift_confirmed != null;
        if (confirmed) {
          const confirmedLabel = document.createElement('p');
          confirmedLabel.className = 'cart-rewards-section__confirmed';
          confirmedLabel.textContent = '✓ Size confirmed';
          info.appendChild(confirmedLabel);
        } else {
          const confirmButton = document.createElement('button');
          confirmButton.type = 'button';
          confirmButton.className = 'cart-rewards-section__confirm button';
          confirmButton.textContent = 'Confirm this size';
          confirmButton.addEventListener('click', () => this.#confirmGift(claimedLine));
          info.appendChild(confirmButton);
        }

        // Two-step opt-out: first click arms it, second click (within the
        // window) commits. A stray extra click after the window lapses just
        // re-arms rather than accidentally opting out.
        const optOutButton = document.createElement('button');
        optOutButton.type = 'button';
        optOutButton.className = 'cart-rewards-section__optout button-unstyled';
        optOutButton.textContent = 'No thanks';
        let pendingOptOut = false;
        /** @type {ReturnType<typeof setTimeout> | null} */
        let pendingTimeout = null;
        optOutButton.addEventListener('click', () => {
          if (!pendingOptOut) {
            pendingOptOut = true;
            optOutButton.textContent = 'Are you sure? Skip this gift';
            optOutButton.classList.add('cart-rewards-section__optout--pending');
            pendingTimeout = setTimeout(() => {
              pendingOptOut = false;
              optOutButton.textContent = 'No thanks';
              optOutButton.classList.remove('cart-rewards-section__optout--pending');
            }, 4000);
          } else {
            if (pendingTimeout) clearTimeout(pendingTimeout);
            this.#optOutGift(claimedLine, tier, slotIndex);
          }
        });
        info.appendChild(optOutButton);
      } else if (!isActive) {
        const chooseButton = document.createElement('button');
        chooseButton.type = 'button';
        chooseButton.className = 'cart-rewards-section__choose button-secondary';
        chooseButton.textContent = 'Choose this instead';
        chooseButton.addEventListener('click', () => {
          const defaultVariant = option.variants.find((v) => v.available) ?? option.variants[0];
          if (!defaultVariant) return;
          if (claimedLine) this.#swapVariant(claimedLine, defaultVariant.id);
          else this.#claimSpecificGift(defaultVariant.id);
        });
        info.appendChild(chooseButton);
      }

      optionRow.appendChild(info);

      const priceCol = document.createElement('div');
      priceCol.className = 'cart-rewards-section__card-price';
      const freeBadge = document.createElement('span');
      freeBadge.className = 'cart-rewards-section__free-badge';
      freeBadge.textContent = 'Free';
      priceCol.appendChild(freeBadge);
      optionRow.appendChild(priceCol);

        optionCard.appendChild(optionRow);
        stack.appendChild(optionCard);
      }
    }

    card.appendChild(stack);

    return card;
  }

  /**
   * Swap the claimed gift for a different variant of the same product (e.g.
   * a different size/color). Adds the new variant before removing the old
   * one so the tier reads as "fulfilled" throughout — otherwise the sticky
   * bar's own reconciliation could momentarily see it as unearned and
   * auto-claim a default variant in the gap, racing this swap.
   * @param {{ key: string, variant_id: number }} claimedLine
   * @param {number} newVariantId
   */
  async #swapVariant(claimedLine, newVariantId) {
    await this.#waitUntilIdle();
    this.#busy = true;
    try {
      const sectionIds = cartItemsSectionIds();
      const addResponse = await addGiftLine(newVariantId, sectionIds);
      if (addResponse.status) {
        console.warn('[cart-rewards-section] could not swap gift variant:', addResponse.description);
        return;
      }
      const removeResponse = await removeGiftLine(claimedLine, sectionIds);
      await dispatchCartLinesUpdate('remove', this, { id: claimedLine.key, quantity: 0 }, removeResponse.sections);
    } catch (error) {
      console.warn('[cart-rewards-section] variant swap failed:', error);
    } finally {
      this.#busy = false;
      this.#lastRenderKey = null;
      await this.#refresh();
    }
  }

  /**
   * Explicitly confirm the currently-claimed gift variant. Sets
   * `_gift_confirmed` on the line (by replacing it — Shopify cart lines
   * can't have their properties edited in place, only add+remove). Required
   * before checkout: cart-validation blocks any earned gift line lacking
   * this property, even if it's otherwise a valid, eligible variant.
   * @param {{ key: string, variant_id: number }} claimedLine
   */
  async #confirmGift(claimedLine) {
    await this.#waitUntilIdle();
    this.#busy = true;
    try {
      const sectionIds = cartItemsSectionIds();
      const addResponse = await addGiftLine(claimedLine.variant_id, sectionIds, true);
      if (addResponse.status) {
        console.warn('[cart-rewards-section] could not confirm gift:', addResponse.description);
        return;
      }
      const removeResponse = await removeGiftLine(claimedLine, sectionIds);
      await dispatchCartLinesUpdate('remove', this, { id: claimedLine.key, quantity: 0 }, removeResponse.sections);
    } catch (error) {
      console.warn('[cart-rewards-section] gift confirmation failed:', error);
    } finally {
      this.#busy = false;
      this.#lastRenderKey = null;
      await this.#refresh();
    }
  }

  /**
   * Declines a single slot's gift: removes just that line and records the
   * decline as a cart attribute so the sticky bar's auto-claim doesn't
   * immediately re-add it. Other slots in the same tier (e.g. the matching
   * set's other item) are untouched. See `#undeclineSlot` to reverse.
   * @param {{ key: string }} claimedLine
   * @param {Tier} tier
   * @param {number} slotIndex
   */
  async #optOutGift(claimedLine, tier, slotIndex) {
    await this.#waitUntilIdle();
    this.#busy = true;
    try {
      const sectionIds = cartItemsSectionIds();
      const cart = await fetchCart();
      const declined = new Set(parseDeclinedSlots(cart));
      declined.add(declinedSlotKey(tier.thresholdCents, slotIndex));

      await removeGiftLine(claimedLine, sectionIds);
      const updateResponse = await setDeclinedSlots(declined, sectionIds);
      await dispatchCartLinesUpdate(
        'remove',
        this,
        { id: claimedLine.key, quantity: 0 },
        updateResponse.sections
      );
    } catch (error) {
      console.warn('[cart-rewards-section] opt-out failed:', error);
    } finally {
      this.#busy = false;
      this.#lastRenderKey = null;
      await this.#refresh();
    }
  }

  /**
   * Reverses a decline on one slot: clears its cart-attribute flag and
   * claims the default variant for just that slot.
   * @param {Tier} tier
   * @param {number} slotIndex
   */
  async #undeclineSlot(tier, slotIndex) {
    await this.#waitUntilIdle();
    this.#busy = true;
    try {
      const sectionIds = cartItemsSectionIds();
      const cart = await fetchCart();
      const declined = new Set(parseDeclinedSlots(cart));
      declined.delete(declinedSlotKey(tier.thresholdCents, slotIndex));
      await setDeclinedSlots(declined, sectionIds);

      const slot = tier.slots[slotIndex];
      const firstOption = slot?.[0];
      if (firstOption) {
        let product = null;
        try {
          product = await fetchProductByHandle(firstOption.productHandle, firstOption.variantIds);
        } catch (error) {
          console.warn('[cart-rewards-section] could not load gift product:', error);
        }
        const defaultVariantId =
          product?.variants.find((v) => v.available)?.id ?? product?.variants[0]?.id ?? firstOption.variantIds[0];
        if (defaultVariantId != null) {
          const response = await addGiftLine(defaultVariantId, sectionIds);
          if (!response.status) {
            await dispatchCartLinesUpdate(
              'add',
              this,
              { merchandiseId: String(defaultVariantId), quantity: 1 },
              response.sections
            );
          }
        }
      }
    } catch (error) {
      console.warn('[cart-rewards-section] undecline failed:', error);
    } finally {
      this.#busy = false;
      this.#lastRenderKey = null;
      await this.#refresh();
    }
  }

  /**
   * Fallback for the rare case a product tab is clicked before the sticky
   * bar's auto-claim has landed (no existing gift line to swap from yet).
   * @param {number} variantId
   */
  async #claimSpecificGift(variantId) {
    await this.#waitUntilIdle();
    this.#busy = true;
    try {
      const sectionIds = cartItemsSectionIds();
      const response = await addGiftLine(variantId, sectionIds);
      if (response.status) {
        console.warn('[cart-rewards-section] could not claim gift:', response.description);
        return;
      }
      await dispatchCartLinesUpdate('add', this, { merchandiseId: String(variantId), quantity: 1 }, response.sections);
    } catch (error) {
      console.warn('[cart-rewards-section] claim failed:', error);
    } finally {
      this.#busy = false;
      this.#lastRenderKey = null;
      await this.#refresh();
    }
  }
}

if (!customElements.get('cart-rewards-bar')) {
  customElements.define('cart-rewards-bar', CartRewardsBarComponent);
}
if (!customElements.get('cart-rewards-section')) {
  customElements.define('cart-rewards-section', CartRewardsSectionComponent);
}
